import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { loginFailure, loginStart, loginSuccess } from "../../redux/userRedux";
import { useDispatch, useSelector } from "react-redux";
import useIsMobile from "../../hooks/useIsMobile";
import getErrorMessage from "../../utils/errorUtils";
import ForgotPasswordForm from "./ForgotPasswordForm";
import LoginFormInputFields from "./LoginFormInputFields";
import { ref, set, get, update } from "firebase/database";
import { database } from "../../firebase";
import { anatomyConstants } from "../../constants/anatomyConstants";
import heroImage from "../../assets/hero.jpeg";

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isFetching, error } = useSelector((state) => state.user);
  const [handleError, setHandleError] = useState("");
  const [resetState, setResetState] = useState({
    resetEmailSent: false,
    resetEmailError: "",
    forgotMyPassword: false,
  });
  const isMobile = useIsMobile();

  const handleSignIn = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;

    try {
      dispatch(loginStart());
      signInWithEmailAndPassword(auth, email, password)
        .then((userCredential) => {
          const user = userCredential.user;
          if (user.emailVerified) {
            const uid = user.uid;
            dispatch(loginSuccess(uid));
            navigate("/home");
          } else {
            dispatch(loginFailure(true));
            setHandleError("Please verify your email before signing in.");
          }
        })
        .catch((error) => {
          dispatch(loginFailure(true));
          setHandleError(getErrorMessage(error.message));
          setTimeout(() => {
            setHandleError("");
          }, 5000);
        });
    } catch (error) {
      dispatch(loginFailure(error.message));
      setHandleError(getErrorMessage(error.message));
      setTimeout(() => {
        setHandleError("");
      }, 5000);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");

    try {
      dispatch(loginStart());

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const uid = user.uid;
      const googleUserData = {
        id: uid,
        email: user.email,
        firstName: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
        number: user.phoneNumber || "",
        department: "",
        institutionName: "",
        cases: {
          0: "-O9Di_x-F7YpJFGaY-1d",
          1: "-OU_VsesW8frMeK-OoDb",
          2: "-OJbrL3KFIO2yBhEtDe5",
        },
      };

      const userRef = ref(database, `users/${uid}`);
      const snapshot = await get(userRef);

      if (!snapshot.exists()) {
        await set(userRef, googleUserData);
      } else {
        const existingData = snapshot.val();
        const updatedData = {
          ...existingData,
          email: existingData.email || googleUserData.email,
          firstName: existingData.firstName || googleUserData.firstName,
          lastName: existingData.lastName || googleUserData.lastName,
        };
        await update(userRef, updatedData);
      }
      dispatch(loginSuccess(uid));
      navigate("/home");
    } catch (error) {
      dispatch(loginFailure(true));
      setHandleError(getErrorMessage(error.message));
      setTimeout(() => {
        setHandleError("");
      }, 5000);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const email = e.target.resetEmail.value;
    try {
      sendPasswordResetEmail(auth, email)
        .then(() => {
          setResetState((prevState) => ({
            ...prevState,
            resetEmailSent: true,
            resetEmailError: "",
          }));
          setTimeout(() => {
            setResetState((prevState) => ({
              ...prevState,
              resetEmailSent: false,
              forgotMyPassword: false,
            }));
          }, 5000);
        })
        .catch((error) => {
          setResetState((prevState) => ({
            ...prevState,
            resetEmailSent: false,
            resetEmailError: getErrorMessage(error.message),
          }));
          setTimeout(() => {
            setResetState((prevState) => ({
              ...prevState,
              resetEmailError: "",
            }));
          }, 5000);
        });
    } catch (error) {
      setResetState((prevState) => ({
        ...prevState,
        resetEmailSent: false,
        resetEmailError: getErrorMessage(error.message),
      }));
      setTimeout(() => {
        setResetState((prevState) => ({
          ...prevState,
          resetEmailError: "",
        }));
      }, 5000);
    }
  };

  const goBackToLogin = () => {
    setResetState({
      resetEmailSent: false,
      resetEmailError: "",
      forgotMyPassword: false,
    });
  };

  const goForgotMyPassword = () => {
    setHandleError("");
    setResetState((prevState) => ({
      ...prevState,
      forgotMyPassword: true,
    }));
  };

  /* ---------- MOBILE ---------- */
  if (isMobile) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: "#0B0E13" }}
      >
        {/* Top gradient glow */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "600px",
            height: "300px",
            background:
              "radial-gradient(ellipse at center, rgba(48,166,156,0.12) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div className="flex-1 flex flex-col justify-center px-6 py-10 relative z-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1
              style={{
                fontFamily: "'Instrument Serif', Georgia, serif",
                fontWeight: 400,
                fontSize: "28px",
                letterSpacing: "-0.01em",
                color: "rgba(255, 255, 255, 1)",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                margin: 0,
              }}
            >
              Cardiowell
            </h1>
            <p className="text-sm mt-1" style={{ color: "#64748B" }}>
              Medical Imaging Platform
            </p>
          </div>

          {/* Card */}
          <div
            className="rounded-2xl p-6 w-full max-w-md mx-auto"
            style={{
              backgroundColor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.1)",
              backdropFilter: "blur(12px)",
            }}
          >
            <h2
              className="text-xl font-semibold text-center mb-6"
              style={{ color: "#E7EBEF" }}
            >
              {resetState.forgotMyPassword ? "Reset Password" : "Log In"}
            </h2>

            {resetState.forgotMyPassword ? (
              <ForgotPasswordForm
                handleResetPassword={handleResetPassword}
                resetEmailSent={resetState.resetEmailSent}
                resetEmailError={resetState.resetEmailError}
                goBackToLogin={goBackToLogin}
              />
            ) : (
              <LoginFormInputFields
                handleSignIn={handleSignIn}
                isFetching={isFetching}
                goForgotMyPassword={goForgotMyPassword}
                error={error}
                handleError={handleError}
                handleGoogleSignIn={handleGoogleSignIn}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ---------- DESKTOP ---------- */
  return (
    <div
      style={{ position: "relative", overflow: "hidden", height: "100%", width: "100%", backgroundColor: "#0B0E13" }}
    >
      {/* ── Left side decorative green gradients ── */}
      <div style={{
        position: "absolute", top: "-15%", left: "-8%",
        width: "520px", height: "520px", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(48,166,156,0.10) 0%, transparent 65%)",
      }} />
      <div style={{
        position: "absolute", bottom: "5%", left: "5%",
        width: "340px", height: "340px", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(48,166,156,0.07) 0%, transparent 65%)",
      }} />

      {/* ── Hero image: right half of screen, with margin from right edge ── */}
      {/* Positioned from 52% to 94% horizontally, full height, blurred silhouette */}
      <div style={{
        position: "absolute",
        top: 0, bottom: 0,
        left: "52%", right: "6%",
        backgroundImage: `url(${heroImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
        opacity: 0.22,
        filter: "blur(3px) grayscale(20%)",
        pointerEvents: "none",
        borderRadius: "0 0 0 0",
      }} />
      {/* Fade the image edges: left edge of the image area fades to dark */}
      <div style={{
        position: "absolute",
        top: 0, bottom: 0,
        left: "48%", width: "200px",
        background: "linear-gradient(to right, #0B0E13 0%, transparent 100%)",
        pointerEvents: "none",
      }} />
      {/* Fade right edge of image into dark */}
      <div style={{
        position: "absolute",
        top: 0, bottom: 0,
        right: "6%", width: "120px",
        background: "linear-gradient(to left, #0B0E13 0%, transparent 100%)",
        pointerEvents: "none",
      }} />
      {/* Fade top of image */}
      <div style={{
        position: "absolute",
        top: 0, height: "120px",
        left: "52%", right: "6%",
        background: "linear-gradient(to bottom, #0B0E13 0%, transparent 100%)",
        pointerEvents: "none",
      }} />
      {/* Fade bottom of image */}
      <div style={{
        position: "absolute",
        bottom: 0, height: "120px",
        left: "52%", right: "6%",
        background: "linear-gradient(to top, #0B0E13 0%, transparent 100%)",
        pointerEvents: "none",
      }} />

      {/* ── Form centered in the middle of the page ── */}
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10,
      }}>
        <div style={{ width: "420px" }}>
          <div style={{ textAlign: "center", marginBottom: "28px" }}>
            <h1 style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontWeight: 400, fontSize: "32px",
              letterSpacing: "-0.01em",
              color: "rgba(255,255,255,1)",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              margin: 0,
            }}>Cardiowell</h1>
            <p style={{ color: "#64748B", fontSize: "14px", marginTop: "6px" }}>
              AI-Powered Medical Imaging
            </p>
          </div>

          {/* Glassmorphism card */}
          <div style={{
            borderRadius: "18px", padding: "32px",
            backgroundColor: "rgba(13, 17, 23, 0.88)",
            border: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 0 0 1px rgba(48,166,156,0.1), 0 24px 48px rgba(0,0,0,0.5)",
          }}>
            <h2 style={{ color: "#E7EBEF", fontSize: "20px", fontWeight: 600, textAlign: "center", marginBottom: "24px" }}>
              {resetState.forgotMyPassword ? "Reset Password" : "Log In"}
            </h2>

            {resetState.forgotMyPassword ? (
              <ForgotPasswordForm
                handleResetPassword={handleResetPassword}
                resetEmailSent={resetState.resetEmailSent}
                resetEmailError={resetState.resetEmailError}
                goBackToLogin={goBackToLogin}
              />
            ) : (
              <LoginFormInputFields
                handleSignIn={handleSignIn}
                isFetching={isFetching}
                goForgotMyPassword={goForgotMyPassword}
                error={error}
                handleError={handleError}
                handleGoogleSignIn={handleGoogleSignIn}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
